import { createFileRoute, Link } from "@tanstack/react-router";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { useOrgPath } from "@/hooks/useOrgPath";
import { Layers, AlertCircle } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { ProviderPoolSelect } from "@/components/Selects/provider-pool-select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type TeamMember = {
  id: string;
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  role: {
    id: string;
    name: string;
  } | null;
};

// ─── Provider Pool Access Section ────────────────────────────────────────────

type PoolAccessMode = "all" | "restricted";

function ProviderPoolAccessSection({
  mode,
  selectedPools,
  onModeChange,
  onSelectedPoolsChange,
  isAdminMember,
  error,
}: {
  mode: PoolAccessMode;
  selectedPools: { id: string; name: string }[];
  onModeChange: (mode: PoolAccessMode) => void;
  onSelectedPoolsChange: (pools: { id: string; name: string }[]) => void;
  isAdminMember: boolean;
  error?: string;
}) {
  if (isAdminMember) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
        <p className="text-base font-semibold text-slate-700 mb-1">
          Provider pool access
        </p>
        <p className="text-sm text-muted-foreground">
          Admins always see all providers via their role.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Label className="text-base font-semibold">Provider pool access</Label>
      <RadioGroup
        value={mode}
        onValueChange={(value) => onModeChange(value as PoolAccessMode)}
        className="gap-2"
      >
        <label className="flex items-center gap-2 cursor-pointer">
          <RadioGroupItem value="all" id="pool-access-all" />
          <span className="text-base">All providers in this business unit</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <RadioGroupItem value="restricted" id="pool-access-restricted" />
          <span className="text-base">Only providers in selected pools</span>
        </label>
      </RadioGroup>

      {mode === "restricted" && (
        <div className="ml-6 space-y-1">
          <ProviderPoolSelect
            isMulti
            value={selectedPools}
            onChange={onSelectedPoolsChange}
            placeholder="Select provider pools..."
          />
          {error && (
            <div className="flex items-center gap-1.5 mt-2 text-red-600">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Pool Access Cell ─────────────────────────────────────────────────────────

const CHAR_LIMIT = 28;

function buildTruncatedLabel(names: string[]): string {
  if (names.length === 0) {
    return "";
  }
  let label = names[0];
  let omitted = false;
  for (let i = 1; i < names.length; i++) {
    const candidate = label + ", " + names[i];
    if (candidate.length > CHAR_LIMIT) {
      omitted = true;
      break;
    }
    label = candidate;
  }
  return omitted ? label + "…" : label;
}

function PoolAccessCell({
  organizationBusinessUnitId,
  organizationBusinessUnitUserId,
}: {
  organizationBusinessUnitId: string;
  organizationBusinessUnitUserId: string;
}) {
  const trpc = useTRPC();
  const { data: assignment, isLoading } = useQuery(
    trpc.organization.getTeamMemberProviderPools.queryOptions({
      organizationBusinessUnitId,
      organizationBusinessUnitUserId,
    }),
  );

  if (isLoading) {
    return <Skeleton className="h-5 w-20" />;
  }

  const pools = assignment ?? [];

  if (pools.length === 0) {
    return (
      <Badge variant="secondary" size="small" className="whitespace-nowrap">
        <Layers className="h-3 w-3 mr-1" />
        All providers
      </Badge>
    );
  }

  const names = pools.map((p) => p.name);
  const label = buildTruncatedLabel(names);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-left text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        <p className="text-xs text-muted-foreground mb-2">
          Provider pools ({pools.length})
        </p>
        <div className="flex flex-wrap gap-1">
          {names.map((name) => (
            <Badge key={name} variant="secondary" size="small">
              {name}
            </Badge>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Edit Member Dialog ───────────────────────────────────────────────────────

const LAST_ADMIN_REMOVE_MESSAGE =
  "Cannot remove the last admin. Assign another team member as admin first.";

function RemoveMemberButton({
  isLastAdmin,
  onClick,
}: {
  isLastAdmin: boolean;
  onClick: () => void;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant="destructive"
              onClick={onClick}
              disabled={isLastAdmin}
            >
              Remove
            </Button>
          </span>
        </TooltipTrigger>
        {isLastAdmin && (
          <TooltipContent className="max-w-72">
            {LAST_ADMIN_REMOVE_MESSAGE}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

function EditMemberDialog({
  member,
  availableRoles,
  isLastAdmin,
  isAdminMember,
  organizationBusinessUnitId,
  onClose,
}: {
  member: TeamMember;
  availableRoles: { id: string; name: string; isDefault: boolean }[];
  isLastAdmin: boolean;
  isAdminMember: boolean;
  organizationBusinessUnitId: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: currentAssignment = [] } = useQuery(
    trpc.organization.getTeamMemberProviderPools.queryOptions({
      organizationBusinessUnitId,
      organizationBusinessUnitUserId: member.id,
    }),
  );

  const [selectedRoleId, setSelectedRoleId] = useState<string>(
    member.role?.id ?? "no-role",
  );
  const [poolMode, setPoolMode] = useState<PoolAccessMode>(
    currentAssignment.length > 0 ? "restricted" : "all",
  );
  const [selectedPools, setSelectedPools] = useState<
    { id: string; name: string }[]
  >(currentAssignment.map((a) => ({ id: a.providerPoolId, name: a.name })));
  const [poolError, setPoolError] = useState<string | undefined>();

  // Once currentAssignment loads, sync initial state
  const [initialised, setInitialised] = useState(false);
  if (!initialised && currentAssignment.length > 0) {
    setPoolMode("restricted");
    setSelectedPools(
      currentAssignment.map((a) => ({ id: a.providerPoolId, name: a.name })),
    );
    setInitialised(true);
  }

  const updateRoleMutation = useMutation(
    trpc.organization.updateTeamMemberRole.mutationOptions(),
  );
  const setPoolsMutation = useMutation(
    trpc.organization.setTeamMemberProviderPools.mutationOptions(),
  );

  const isPending = updateRoleMutation.isPending || setPoolsMutation.isPending;

  const handleSelectedPoolsChange = (pools: { id: string; name: string }[]) => {
    setSelectedPools(pools);
    setPoolError(undefined);
  };

  const handleSave = async () => {
    if (poolMode === "restricted" && selectedPools.length === 0) {
      setPoolError("Select at least one pool");
      return;
    }

    const roleChanged = selectedRoleId !== (member.role?.id ?? "no-role");
    if (roleChanged && selectedRoleId !== "no-role") {
      await updateRoleMutation.mutateAsync({
        organizationBusinessUnitId,
        organizationBusinessUnitUserId: member.id,
        roleId: selectedRoleId,
      });
    }

    await setPoolsMutation.mutateAsync({
      organizationBusinessUnitId,
      organizationBusinessUnitUserId: member.id,
      providerPoolIds:
        poolMode === "restricted" ? selectedPools.map((p) => p.id) : [],
    });

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.organization.getTeamMembers.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.organization.getTeamMemberProviderPools.queryKey(),
      }),
    ]);

    onClose();
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium mb-1.5 block">Role</Label>
        <Select
          value={selectedRoleId}
          onValueChange={setSelectedRoleId}
          disabled={isPending || isLastAdmin}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a role" />
          </SelectTrigger>
          <SelectContent>
            {!member.role && (
              <SelectItem value="no-role" disabled>
                No role assigned
              </SelectItem>
            )}
            {availableRoles.map((role) => (
              <SelectItem key={role.id} value={role.id}>
                {role.name}
                {role.isDefault && " (Default)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isLastAdmin && (
          <p className="text-xs text-muted-foreground mt-1">
            At least one admin is required
          </p>
        )}
      </div>

      <ProviderPoolAccessSection
        mode={poolMode}
        selectedPools={selectedPools}
        onModeChange={setPoolMode}
        onSelectedPoolsChange={handleSelectedPoolsChange}
        isAdminMember={isAdminMember}
        error={poolError}
      />

      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isPending}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isPending}
          className="w-full sm:w-auto"
        >
          {isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function TeamPage() {
  const { user } = Route.useRouteContext();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { hasPermission } = useRolePermissions(user);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const orgPath = useOrgPath();

  const canManageTeam = hasPermission(Permission.ManageTeamMembers);
  const canViewTeam = hasPermission(Permission.ViewTeamMembers);

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [emailsInput, setEmailsInput] = useState("");
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [memberToEdit, setMemberToEdit] = useState<TeamMember | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(
    undefined,
  );

  // Invite dialog pool access state
  const [invitePoolMode, setInvitePoolMode] = useState<PoolAccessMode>("all");
  const [inviteSelectedPools, setInviteSelectedPools] = useState<
    { id: string; name: string }[]
  >([]);
  const [invitePoolError, setInvitePoolError] = useState<string | undefined>();

  const { data: members = [], isLoading: membersLoading } = useQuery(
    trpc.organization.getTeamMembers.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
  );

  const { data: availableRoles = [] } = useQuery(
    trpc.organization.getBuRoles.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
  );

  // Identify the last admin (member with the default Admin role who is the only one)
  const adminRole = availableRoles.find((r) => r.isDefault);
  const adminMembers = adminRole
    ? members.filter((m) => m.role?.id === adminRole.id)
    : [];
  const lastAdminMemberId =
    adminMembers.length === 1 ? adminMembers[0].id : null;

  const setPoolsMutation = useMutation(
    trpc.organization.setTeamMemberProviderPools.mutationOptions(),
  );

  const inviteMemberMutation = useMutation(
    trpc.organization.inviteTeamMember.mutationOptions({
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: trpc.organization.getTeamMembers.queryKey(),
        });

        // If pool restriction was configured, apply it to the newly invited members
        if (invitePoolMode === "restricted" && inviteSelectedPools.length > 0) {
          const freshMembers = await queryClient.fetchQuery(
            trpc.organization.getTeamMembers.queryOptions({
              organizationBusinessUnitId: selectedOrgBusinessUnit.id,
            }),
          );
          const emails = parseEmails(emailsInput);
          const newMembers = freshMembers.filter((m) =>
            emails.some(
              (email) => m.email?.toLowerCase() === email.toLowerCase(),
            ),
          );
          const providerPoolIds = inviteSelectedPools.map((p) => p.id);
          await Promise.all(
            newMembers.map((m) =>
              setPoolsMutation.mutateAsync({
                organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                organizationBusinessUnitUserId: m.id,
                providerPoolIds,
              }),
            ),
          );
        }

        setIsInviteDialogOpen(false);
        setEmailsInput("");
        setSelectedRoleId(undefined);
        setInvitePoolMode("all");
        setInviteSelectedPools([]);
        setInvitePoolError(undefined);

        if (data.invitedCount > 0) {
          console.log(
            `Successfully invited ${data.invitedCount} team member(s)`,
          );
        }
        if (data.existingCount > 0) {
          console.log(
            `${data.existingCount} member(s) were already in the team`,
          );
        }
      },
    }),
  );

  const removeMemberMutation = useMutation(
    trpc.organization.removeTeamMember.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.organization.getTeamMembers.queryKey(),
        });
        setIsRemoveDialogOpen(false);
        setMemberToRemove(null);
      },
    }),
  );

  const parseEmails = (input: string): string[] => {
    return input
      .split(/[,;\n\s]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const emails = parseEmails(emailsInput);

    if (emails.length === 0) {
      return;
    }

    const invalidEmails = emails.filter((email) => !validateEmail(email));
    if (invalidEmails.length > 0) {
      alert(
        `Invalid email addresses: ${invalidEmails.join(", ")}\n\nPlease fix these and try again.`,
      );
      return;
    }

    if (!selectedRoleId) {
      return;
    }

    if (invitePoolMode === "restricted" && inviteSelectedPools.length === 0) {
      setInvitePoolError("Select at least one pool");
      return;
    }

    inviteMemberMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      emails,
      roleId: selectedRoleId,
    });
  };

  const handleRemoveClick = (member: TeamMember) => {
    setMemberToRemove(member);
    setIsRemoveDialogOpen(true);
  };

  const handleEditClick = (member: TeamMember) => {
    setMemberToEdit(member);
    setIsEditDialogOpen(true);
  };

  const handleConfirmRemove = () => {
    if (!memberToRemove) {
      return;
    }

    removeMemberMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      organizationBusinessUnitUserId: memberToRemove.id,
    });
  };

  const isAdminMember = (member: TeamMember) =>
    adminRole != null && member.role?.id === adminRole.id;

  // If user can't view team, show access denied
  if (!canViewTeam) {
    return (
      <div className="container mx-auto py-8 px-2">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-500 bg-gray-50 rounded-lg">
            <svg
              className="w-12 h-12 mb-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
            <p className="text-sm text-center">
              You don't have permission to view team members.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-2">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold truncate">
                {selectedOrgBusinessUnit.name}
              </h1>
              <h2 className="text-lg text-gray-600 mt-1">Team Members</h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              {hasPermission(Permission.ManageBuSettings) && (
                <Link
                  to={orgPath("/team/roles")}
                  className={buttonVariants({ variant: "outline" })}
                >
                  Manage Roles
                </Link>
              )}
              {canManageTeam && (
                <Dialog
                  open={isInviteDialogOpen}
                  onOpenChange={setIsInviteDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button>Invite Members</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:min-w-[500px] p-4 sm:p-6">
                    <DialogHeader>
                      <DialogTitle>Invite Team Members</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleInviteSubmit} className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium mb-1.5 block">
                          Email Addresses
                        </Label>
                        <Textarea
                          value={emailsInput}
                          onChange={(e) => setEmailsInput(e.target.value)}
                          placeholder="Enter email addresses (one per line or separated by commas)"
                          rows={4}
                          className="resize-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          You can enter multiple email addresses separated by
                          commas, spaces, or on separate lines.
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium mb-1.5 block">
                          Role
                        </Label>
                        <Select
                          value={selectedRoleId}
                          onValueChange={setSelectedRoleId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableRoles.map((role) => (
                              <SelectItem key={role.id} value={role.id}>
                                {role.name}
                                {role.isDefault && " (Default)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <ProviderPoolAccessSection
                        mode={invitePoolMode}
                        selectedPools={inviteSelectedPools}
                        onModeChange={setInvitePoolMode}
                        onSelectedPoolsChange={(pools) => {
                          setInviteSelectedPools(pools);
                          setInvitePoolError(undefined);
                        }}
                        isAdminMember={false}
                        error={invitePoolError}
                      />

                      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsInviteDialogOpen(false)}
                          className="w-full sm:w-auto"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={
                            inviteMemberMutation.isPending ||
                            setPoolsMutation.isPending ||
                            emailsInput.trim().length === 0 ||
                            !selectedRoleId
                          }
                          className="w-full sm:w-auto"
                        >
                          {inviteMemberMutation.isPending
                            ? "Inviting..."
                            : "Send Invitations"}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>

        {membersLoading ? (
          <>
            {/* Mobile skeleton cards */}
            <div className="block lg:hidden space-y-3">
              {[...Array(3)].map((_, index) => (
                <Card key={index} className="p-4 space-y-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="space-y-2 flex-1 min-w-0">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-4 w-16 flex-shrink-0" />
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <Skeleton className="h-8 w-full" />
                    {canManageTeam && (
                      <Skeleton className="h-8 w-16 flex-shrink-0" />
                    )}
                  </div>
                </Card>
              ))}
            </div>
            {/* Desktop skeleton table */}
            <Card className="hidden lg:block overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Pool Access</TableHead>
                    <TableHead>Joined</TableHead>
                    {canManageTeam && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array(3)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-48" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      {canManageTeam && (
                        <TableCell>
                          <Skeleton className="h-12 w-16 rounded-xl" />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        ) : !members || members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-500 bg-gray-50 rounded-lg">
            <svg
              className="w-12 h-12 mb-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold mb-2">No Team Members Yet</h3>
            <p className="text-sm text-center mb-4">
              {canManageTeam
                ? "Get started by inviting members to your team"
                : "This team doesn't have any members yet"}
            </p>
            {canManageTeam && (
              <Button
                onClick={() => setIsInviteDialogOpen(true)}
                variant="outline"
                size="sm"
              >
                Invite Members
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile / tablet card layout */}
            <div className="block lg:hidden space-y-3">
              {members.map((member: TeamMember) => {
                const isLastAdmin = member.id === lastAdminMemberId;
                const displayName =
                  member.firstName || member.lastName
                    ? `${member.firstName || ""} ${member.lastName || ""}`.trim()
                    : "—";
                return (
                  <Card key={member.id} className="p-4 space-y-3">
                    <div className="flex justify-between items-start gap-3 min-w-0">
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <p className="font-medium text-sm truncate">
                          {displayName}
                        </p>
                        <p className="text-xs text-muted-foreground break-all">
                          {member.email ?? "—"}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground flex-shrink-0 pt-0.5">
                        {new Date(member.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {canManageTeam && (
                      <div className="flex items-center gap-2">
                        <PoolAccessCell
                          organizationBusinessUnitId={
                            selectedOrgBusinessUnit.id
                          }
                          organizationBusinessUnitUserId={member.id}
                        />
                      </div>
                    )}
                    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm">
                          {member.role?.name || "No role assigned"}
                        </span>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {canManageTeam && (
                          <Button
                            variant="outline"
                            onClick={() => handleEditClick(member)}
                          >
                            Edit
                          </Button>
                        )}
                        {canManageTeam && (
                          <RemoveMemberButton
                            isLastAdmin={isLastAdmin}
                            onClick={() => handleRemoveClick(member)}
                          />
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Desktop table layout */}
            <Card className="hidden lg:block overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Pool Access</TableHead>
                    <TableHead>Joined</TableHead>
                    {canManageTeam && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member: TeamMember) => {
                    const isLastAdmin = member.id === lastAdminMemberId;
                    return (
                      <TableRow key={member.id}>
                        <TableCell>
                          {member.firstName || member.lastName
                            ? `${member.firstName || ""} ${member.lastName || ""}`.trim()
                            : "—"}
                        </TableCell>
                        <TableCell>{member.email ?? "—"}</TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {member.role?.name || "No role assigned"}
                          </span>
                        </TableCell>
                        <TableCell>
                          {canManageTeam ? (
                            <PoolAccessCell
                              organizationBusinessUnitId={
                                selectedOrgBusinessUnit.id
                              }
                              organizationBusinessUnitUserId={member.id}
                            />
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(member.createdAt).toLocaleDateString()}
                        </TableCell>
                        {canManageTeam && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                onClick={() => handleEditClick(member)}
                              >
                                Edit
                              </Button>
                              <RemoveMemberButton
                                isLastAdmin={isLastAdmin}
                                onClick={() => handleRemoveClick(member)}
                              />
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </>
        )}

        {/* Edit Member Dialog */}
        {canManageTeam && memberToEdit && (
          <Dialog
            open={isEditDialogOpen}
            onOpenChange={(open) => {
              setIsEditDialogOpen(open);
              if (!open) {
                setMemberToEdit(null);
              }
            }}
          >
            <DialogContent className="sm:min-w-[500px] p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle>
                  Edit{" "}
                  {memberToEdit.firstName || memberToEdit.lastName
                    ? `${memberToEdit.firstName || ""} ${memberToEdit.lastName || ""}`.trim()
                    : memberToEdit.email}
                </DialogTitle>
              </DialogHeader>
              <EditMemberDialog
                member={memberToEdit}
                availableRoles={availableRoles}
                isLastAdmin={memberToEdit.id === lastAdminMemberId}
                isAdminMember={isAdminMember(memberToEdit)}
                organizationBusinessUnitId={selectedOrgBusinessUnit.id}
                onClose={() => {
                  setIsEditDialogOpen(false);
                  setMemberToEdit(null);
                }}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Remove Member Dialog */}
        {canManageTeam && (
          <Dialog
            open={isRemoveDialogOpen}
            onOpenChange={setIsRemoveDialogOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove Team Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p>
                  Are you sure you want to remove{" "}
                  {memberToRemove?.firstName || memberToRemove?.lastName
                    ? `${memberToRemove?.firstName || ""} ${memberToRemove?.lastName || ""}`.trim()
                    : memberToRemove?.email}{" "}
                  from this team?
                </p>
                <div className="flex justify-end gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsRemoveDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleConfirmRemove}
                    disabled={removeMemberMutation.isPending}
                  >
                    {removeMemberMutation.isPending ? "Removing..." : "Remove"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/team/",
)({
  component: TeamPage,
});
